import { useCallback, useRef, useState } from "react";

import { aiAgentQueryKeys } from "app/pages/sales-intelligence/modules/ai-agent/queries/queryKeyFactory";
import { useSSEStream } from "app/pages/sales-intelligence/modules/ai-agent/queries/useSSEStream";
import ParseJSONService from "app/pages/sales-intelligence/modules/ai-agent/services/jsonParser/jsonParserService";
import type { ParsedAIEmailPreview } from "app/pages/sales-intelligence/modules/ai-agent/services/jsonParser/types";
import ParseEventStreamService from "app/pages/sales-intelligence/modules/ai-agent/services/streamParser/streamParserService";
import type { StreamEvent } from "app/pages/sales-intelligence/modules/ai-agent/services/streamParser/types";
import type { Competitor } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";

type MutateArgs = { threadId: string; competitors: Competitor[] };

const useApplyCompetitorsMutation = ({ onAddVersion }: { onAddVersion: (preview: ParsedAIEmailPreview) => void }) => {
    const [payload, setPayload] = useState<MutateArgs | null>(null);
    const requestIdRef = useRef(0);

    const handleStreamEvent = useCallback(
        (event: StreamEvent) => {
            if (ParseEventStreamService.isPartialMessageStreamEvent(event)) {
                const chunk = event.data[0].content;
                const parsedEmail = ParseJSONService.parseEmail(chunk);
                const { cleaned, json, index } = ParseJSONService.extractEmbeddedJSON(parsedEmail.body);
                if (parsedEmail.subject && parsedEmail.body) {
                    onAddVersion({
                        subject: parsedEmail.subject,
                        body: cleaned,
                        chartJson: json as Record<string, unknown>,
                        chartAnchorIndex: index,
                    });
                }
            }
        },
        [onAddVersion],
    );

    const { isFetching, stop } = useSSEStream({
        enabled: !!payload,
        queryKey: [
            ...aiAgentQueryKeys.applyEmailCompetitors(payload?.threadId, payload?.competitors),
            requestIdRef.current,
        ],
        payload: payload,
        fetcher: salesApiService.aiAgent.email.applyCompetitors,
        onEvent: handleStreamEvent,
        queryOptions: {
            staleTime: 0,
            gcTime: 0,
            retry: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            refetchOnMount: false,
            structuralSharing: false,
            throwOnError: false,
        },
    });

    const mutate = useCallback(
        (args: MutateArgs) => {
            void stop?.();
            requestIdRef.current += 1;
            setPayload(args);
        },
        [stop],
    );

    const reset = useCallback(() => {
        void stop?.();
        setPayload(null);
    }, [stop]);

    return {
        mutate,
        reset,
        isFetching,
        stop,
    };
};

export default useApplyCompetitorsMutation;
